import { Directive, Input, ElementRef, OnDestroy, NgZone, SecurityContext } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";

@Directive({
  selector: "[htmlTooltip]",
})
export class HtmlTooltipDirective implements OnDestroy {
  @Input("htmlTooltip") content: string = "";
  @Input() tooltipPosition: "right" | "left" | "top" | "bottom" = "right";

  private tooltipEl: HTMLElement | null = null;
  private removeMouseenter: (() => void) | null = null;
  private removeMouseleave: (() => void) | null = null;

  constructor(private el: ElementRef<HTMLElement>, private zone: NgZone, private sanitizer: DomSanitizer) {
    this.zone.runOutsideAngular(() => {
      const host = this.el.nativeElement;
      const onEnter = () => this.show();
      const onLeave = () => this.hide();
      host.addEventListener("mouseenter", onEnter);
      host.addEventListener("mouseleave", onLeave);
      this.removeMouseenter = () => host.removeEventListener("mouseenter", onEnter);
      this.removeMouseleave = () => host.removeEventListener("mouseleave", onLeave);
    });
  }

  private show(): void {
    if (!this.content) return;
    this.hide();

    const el = document.createElement("div");
    el.style.position = "fixed";
    el.style.zIndex = "9999";
    el.style.visibility = "hidden";
    el.style.pointerEvents = "none";
    const safeHtml = this.sanitizer.sanitize(SecurityContext.HTML, this.content) ?? "";
    el.innerHTML = safeHtml;
    document.body.appendChild(el);
    this.tooltipEl = el;

    // Position after paint so dimensions are available
    requestAnimationFrame(() => {
      if (!this.tooltipEl) return;
      this.positionTooltip();
      this.tooltipEl.style.visibility = "visible";
    });
  }

  private positionTooltip(): void {
    if (!this.tooltipEl) return;

    const host = this.el.nativeElement.getBoundingClientRect();
    const tt = this.tooltipEl.getBoundingClientRect();
    const gap = 8;

    let top: number;
    let left: number;

    switch (this.tooltipPosition) {
      case "left":
        top = host.top + (host.height - tt.height) / 2;
        left = host.left - tt.width - gap;
        break;
      case "top":
        top = host.top - tt.height - gap;
        left = host.left + (host.width - tt.width) / 2;
        break;
      case "bottom":
        top = host.bottom + gap;
        left = host.left + (host.width - tt.width) / 2;
        break;
      case "right":
      default:
        top = host.top + (host.height - tt.height) / 2;
        left = host.right + gap;
        break;
    }

    // Clamp to viewport
    left = Math.max(4, Math.min(left, window.innerWidth - tt.width - 4));
    top = Math.max(4, Math.min(top, window.innerHeight - tt.height - 4));

    this.tooltipEl.style.top = `${top}px`;
    this.tooltipEl.style.left = `${left}px`;
  }

  private hide(): void {
    if (this.tooltipEl) {
      this.tooltipEl.remove();
      this.tooltipEl = null;
    }
  }

  ngOnDestroy(): void {
    this.hide();
    this.removeMouseenter?.();
    this.removeMouseleave?.();
  }
}
